import axios from 'axios';
import {
  clearAuthStorage,
  getAccessToken,
  navigateToAuthError,
  refreshToken,
} from './authContextProvider';
import { getBackendHost } from './backendHost';
import { beginAuthReauthentication, saveAuthReturnUrl } from './authReturnUrl';

const api = axios.create({
  baseURL: getBackendHost(),
});

// Add a request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor
api.interceptors.response.use(
  (response) => response,
  async (err) => {
    const originalRequest = err.config;
    let error = err;
    // If the error response is a Blob, we need to parse it to JSON. Case export to excel
    if (err.response?.data instanceof Blob) {
      error = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const result = JSON.parse(reader.result as string);
            resolve({response: {status: result.statusCode, data: result}});
          } catch (e) {
            resolve(reader.result);
          }
        };
        reader.onerror = reject;
        reader.readAsText(err.response.data);
      });
    }
    // A backend 401 can be caused by an expired access token or by a token
    // expiring while the request was in flight. Retry once after OIDC renewal.
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await refreshToken();
        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
        return api(originalRequest);
      } catch (err) {
        // Handle refresh token error or redirect to login
        console.log(err);
        handleSession(err as SessionError);
      }
    } else if (originalRequest?.url && !originalRequest.url.startsWith('/v1/admin')) {
      console.log(error);
      handleSession(error);
    }

    return Promise.reject(error);
  }
);

/**
 * Go to login if the token has expired or go to error permisos if the user has not permission
 * @param error 
 */
interface SessionError {
  response?: {
    status?: number;
    data?: {
      statusCode?: number;
      message?: string;
    };
  };
}

export function handleSession(error: SessionError): void {
  const status = error?.response?.status || error?.response?.data?.statusCode;
  if (status === 401) {
    if (!beginAuthReauthentication()) {
      navigateToAuthError('/error-permisos');
      return;
    }

    saveAuthReturnUrl();
    clearAuthStorage();
    window.location.replace('/');
  } else if (status === 403) {
    navigateToAuthError('/error-permisos');
  }
}

export default api;
